import { Alert, AlertTitle, Box, Button, CircularProgress, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { useContext, useEffect, useRef, useState } from 'react';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import { formatLiftAPIClientError } from '~/components/form/utils';
import type { CfiReport, CfiStatus } from '~/data/hooks/useCfi';
import { useCfiMutations, useLatestCfiReport } from '~/data/hooks/useCfi';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

import { CfiBetaNotice } from './cfi/CfiBetaNotice';
import { CfiClientView } from './cfi/CfiClientView';
import { CfiIntro } from './cfi/CfiIntro';
import { PROSE_MAX_WIDTH_WIDE } from './cfi/cfiLayout';
import { CfiReportEditor } from './cfi/CfiReportEditor';
import { CfiReportViewer } from './cfi/CfiReportViewer';
import { CfiRunButton } from './cfi/CfiRunButton';
import { CfiRunningState } from './cfi/CfiRunningState';
import type { ContractReview } from './types';

type Props = {
  contract: ContractReview;
  /**
   * Lay the report out for a full page rather than the 900px drawer this tab was
   * built in. Off by default, so the drawer keeps the layout it has today.
   */
  wide?: boolean;
};

const RUNNING_STATUSES = new Set<CfiReport['status']>(['pending', 'running']);

// Match the surrounding tabs, which wrap their content in `Box sx={{ p: 3 }}`. The
// running/idle states get their own vertically-centered layout so the spinner sits in
// the visual center.
const tabContainerSx = { p: 3, width: '100%' };
const centeredStateSx = {
  p: 3,
  width: '100%',
  minHeight: 320,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const ContractCfiTab = ({ contract, wide = false }: Props) => {
  const { userDetails } = useContext(UserContext);
  const { latest, isLoading, error, mutate } = useLatestCfiReport(contract.id);
  const { run } = useCfiMutations(contract.id);
  const { track } = useAnalytics();
  const [isTriggering, setIsTriggering] = useState(false);
  const { showSnackbar } = useLAISnackbar();

  const isConsultant = userDetails.role === 'consultant';

  // Fire cfi_tab_viewed once per contract, the first time status has loaded —
  // the funnel entry / denominator. Ref-deduped so StrictMode's double-mount
  // and SWR revalidation don't re-fire; capturing report_status at first view.
  const tabViewedFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading || error) return;
    if (tabViewedFiredRef.current === contract.id) return;
    tabViewedFiredRef.current = contract.id;
    track('cfi_tab_viewed', {
      contract_id: contract.id,
      viewer_role: isConsultant ? 'consultant' : 'client',
      report_status: latest?.status ?? 'none',
    });
  }, [isLoading, error, contract.id, isConsultant, latest, track]);

  // Fire cfi_run_completed / cfi_run_failed when the poll observes a run reach
  // a terminal state live (i.e. the tab was open across the transition from
  // pending/running). A report already terminal on first load is skipped:
  // `prevStatusRef` starts null, so the wasRunning guard is false. Deduped per
  // report id so the 3s poll can't re-fire after the transition.
  const prevStatusRef = useRef<CfiStatus | null>(null);
  const runOutcomeFiredRef = useRef<string | null>(null);
  useEffect(() => {
    const status = latest?.status ?? null;
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (!latest) return;
    const wasRunning = prev === 'pending' || prev === 'running';
    if (!wasRunning || runOutcomeFiredRef.current === latest.id) return;
    if (status === 'awaiting_review') {
      runOutcomeFiredRef.current = latest.id;
      track('cfi_run_completed', {
        contract_id: contract.id,
        report_id: latest.id,
        finding_count: latest.findings.length,
      });
    } else if (status === 'failed') {
      runOutcomeFiredRef.current = latest.id;
      track('cfi_run_failed', { contract_id: contract.id, report_id: latest.id });
    }
  }, [latest, contract.id, track]);

  // Show the loading state the instant the user clicks, before the POST resolves.
  const handleRun = async () => {
    track('cfi_run_initiated', {
      contract_id: contract.id,
      trigger: latest?.status === 'failed' ? 'retry' : 'initial',
    });
    setIsTriggering(true);
    try {
      await run();
    } catch (error) {
      // A refusal (e.g. a 403 when the contract covers a property outside the
      // caller's accounts, or a 400 precondition) carries its reason; show it.
      showSnackbar(formatLiftAPIClientError('Could not start ContractIQ', error), {
        variant: 'error',
      });
      if (!(error instanceof LiftAPIClientError) || error.context.status >= 500) {
        captureException(error);
      }
    } finally {
      setIsTriggering(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          ...centeredStateSx,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading ContractIQ status…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={tabContainerSx}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => mutate()} sx={BUTTON_SX}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not load ContractIQ status</AlertTitle>
          {String((error as Error)?.message ?? error)}
        </Alert>
      </Box>
    );
  }

  // Client (non-consultant) users get a read-only view: download the published
  // report, or a prompt to ask their consultant. They never see the run flow,
  // in-progress runs, or consultant work-in-progress drafts.
  if (!isConsultant) {
    return (
      <Box sx={tabContainerSx}>
        <CfiClientView report={latest} wide={wide} />
      </Box>
    );
  }

  if (isTriggering || (latest !== null && RUNNING_STATUSES.has(latest.status))) {
    return (
      <Box sx={centeredStateSx}>
        <CfiRunningState startedAt={latest?.startedAt ?? null} />
      </Box>
    );
  }

  if (!latest) {
    const hasAttachment = (contract.attachments?.length ?? 0) > 0;
    return (
      <Box sx={tabContainerSx}>
        <Box sx={{ maxWidth: wide ? PROSE_MAX_WIDTH_WIDE : 640, mx: 'auto', mb: 1 }}>
          <CfiBetaNotice />
          <CfiIntro />
          <Typography variant="body2" color="text.secondary">
            Expect a run to take a few minutes. You can leave this tab open or come back later
            &mdash; the report is saved when it finishes.
          </Typography>
        </Box>
        <CfiRunButton
          helperText={
            hasAttachment
              ? 'Scores 18 core provisions plus discovered clauses against the consultant rubric.'
              : undefined
          }
          disabled={!hasAttachment}
          disabledReason={hasAttachment ? undefined : 'Attach the contract PDF first.'}
          onRun={handleRun}
        />
      </Box>
    );
  }

  if (latest.status === 'failed') {
    return (
      <Box sx={tabContainerSx}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>The previous run failed</AlertTitle>
          {latest.failureReason || 'Unknown error.'}
        </Alert>
        <CfiRunButton label="Retry" onRun={handleRun} />
      </Box>
    );
  }

  if (latest.status === 'awaiting_review') {
    return (
      <Box sx={tabContainerSx}>
        <CfiReportEditor
          contractId={contract.id}
          report={latest}
          canEdit={isConsultant}
          wide={wide}
        />
      </Box>
    );
  }

  // published, superseded — read-only viewer.
  const canReopen = isConsultant && latest.status === 'published';
  return (
    <Box sx={tabContainerSx}>
      <CfiReportViewer contractId={contract.id} report={latest} canReopen={canReopen} wide={wide} />
    </Box>
  );
};
